import React, { useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/translations';
import useScrollAnimation from '../hooks/useScrollAnimation';
import '../styles/scrollAnimations.css';
import './ExpertSlider.css';

const CertifiedBadge = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7 0.5L8.5 2.5L11 2L11.5 4.5L13.5 5.5L12.5 7.5L13.5 9.5L11.5 10.5L11 12.5L8.5 12L7 14L5.5 12L3 12.5L2.5 10.5L0.5 9.5L1.5 7.5L0.5 5.5L2.5 4.5L3 2L5.5 2.5L7 0.5Z" fill="currentColor" />
    <path d="M5 7L6.5 8.5L9.5 5.5" stroke="#080808" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ExpertCard = ({ expert, certified }) => {
  return (
    <div className="expert-card">
      <div className="expert-card-avatar">
        <img src={expert.image} alt={expert.name} className="expert-card-photo" />
        <img src="./img/verified_2.webp" alt="" className="expert-card-badge" />
        <div className="expert-card-strengths">
          {expert.strengths.map((s, i) => (
            <span key={i} className="expert-card-strength">{s}</span>
          ))}
        </div>
      </div>
      <div className="expert-card-info">
        <div className="expert-card-name-row">
          <h4 className="expert-card-name">{expert.name}</h4>
          <span className="expert-card-level">{expert.level}</span>
        </div>
        <p className="expert-card-role">{expert.role}</p>
      </div>
      <div className="expert-card-ticket">
        <div className="expert-card-ticket-content">
          <CertifiedBadge />
          <span className="expert-card-ticket-text">Certified Bonusclub Growth Partner</span>
        </div>
      </div>
    </div>
  );
};

const HiringCard = ({ hiring }) => {
  const navigate = useNavigate();

  return (
    <div className="expert-card expert-card--hiring" onClick={() => navigate('/apply')}>
      <div className="hiring-card-glow" />
      <div className="hiring-card-content">
        <span className="hiring-card-badge">
          <span className="hiring-card-badge-dot" />
          {hiring.badge}
        </span>
        <div className="hiring-card-icon">
          <div className="hiring-card-icon-ring" />
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
            <path d="M14 7v14M7 14h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <h4 className="hiring-card-title">{hiring.title}</h4>
        <p className="hiring-card-text">{hiring.text}</p>
        <button className="hiring-card-cta">
          {hiring.cta}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const ANIMATION_DURATION = 50;

const ExpertSlider = () => {
  const { language } = useLanguage();
  const t = translations[language].expertSlider;
  const [labelRef, labelVisible] = useScrollAnimation({ threshold: 0.2 });
  const [titleRef, titleVisible] = useScrollAnimation({ threshold: 0.2 });

  const trackRef = useRef(null);
  const dragState = useRef({ active: false, startX: 0, baseX: 0, moved: false });

  const getTrackX = useCallback(() => {
    if (!trackRef.current) return 0;
    const { transform } = getComputedStyle(trackRef.current);
    if (!transform || transform === 'none') return 0;
    return new DOMMatrix(transform).m41;
  }, []);

  const resumeAnimation = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const halfWidth = track.scrollWidth / 2;
    if (halfWidth === 0) return;
    const currentX = getTrackX();
    let progress = (-currentX / halfWidth) % 1;
    if (progress < 0) progress += 1;
    track.style.transform = '';
    track.style.animation = '';
    track.style.animationDelay = `-${progress * ANIMATION_DURATION}s`;
  }, [getTrackX]);

  const handlePointerDown = useCallback((clientX) => {
    const track = trackRef.current;
    if (!track) return;
    dragState.current = { active: true, startX: clientX, baseX: getTrackX(), moved: false };
    track.style.animation = 'none';
    track.style.transform = `translateX(${dragState.current.baseX}px)`;
  }, [getTrackX]);

  const handlePointerMove = useCallback((clientX) => {
    const ds = dragState.current;
    if (!ds.active || !trackRef.current) return;
    const dx = clientX - ds.startX;
    if (Math.abs(dx) > 3) ds.moved = true;
    trackRef.current.style.transform = `translateX(${ds.baseX + dx}px)`;
    if (ds.moved) trackRef.current.classList.add('is-dragging');
  }, []);

  const handlePointerUp = useCallback(() => {
    const ds = dragState.current;
    const track = trackRef.current;
    if (!ds.active || !track) return;
    ds.active = false;
    track.classList.remove('is-dragging');
    resumeAnimation();
  }, [resumeAnimation]);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragState.current.active) return;
      const x = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
      handlePointerMove(x);
    };
    const onEnd = () => handlePointerUp();

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [handlePointerMove, handlePointerUp]);

  const onTrackClick = useCallback((e) => {
    if (dragState.current.moved) {
      e.stopPropagation();
      e.preventDefault();
    }
  }, []);

  const renderCards = (keyPrefix) => (
    <>
      {t.experts.map((expert, i) => (
        <ExpertCard key={`${keyPrefix}-${i}`} expert={expert} certified={t.certified} />
      ))}
      <HiringCard key={`${keyPrefix}-hiring`} hiring={t.hiring} />
    </>
  );

  return (
    <section className="expert-slider-section">
      <div className="expert-slider-header">
        <span
          ref={labelRef}
          className={`section-label scroll-fade-in ${labelVisible ? 'is-visible' : ''}`}
        >
          {t.label}
        </span>
        <h2
          ref={titleRef}
          className={`expert-slider-title scroll-fade-in scroll-animation-delay-1 ${titleVisible ? 'is-visible' : ''}`}
        >
          {t.title}{t.titleLine2 && <><br /><span className="expert-slider-title-gradient">{t.titleLine2}</span></>}
        </h2>
        <p className="expert-slider-subtitle">{t.subtitle}</p>
      </div>

      <div className="expert-slider-track-wrapper">
        <div className="expert-slider-fade expert-slider-fade--left" />
        <div className="expert-slider-fade expert-slider-fade--right" />
        <div
          className="expert-slider-track"
          ref={trackRef}
          onMouseDown={(e) => { e.preventDefault(); handlePointerDown(e.clientX); }}
          onTouchStart={(e) => handlePointerDown(e.touches[0].clientX)}
          onClickCapture={onTrackClick}
        >
          {renderCards('a')}
          {renderCards('b')}
        </div>
      </div>
    </section>
  );
};

export default ExpertSlider;
